import React, { useState, useEffect, useCallback, useMemo } from "react";

/*
  AlokCRM :: SCOT page  (light theme)
  Tabs: Today's Calls | Master List | Monthly Schedule | History
  Same API contract — backend unchanged.
  Props: apiBase (default "" nginx proxy), authToken (else localStorage 'crm_token')
*/

const WEEK = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const PRIORITY = {
  red:    { bg: "#F7E6DE", fg: "#B24A2E", dot: "#CC5A38", label: "High" },
  yellow: { bg: "#FBF0D8", fg: "#9A6B18", dot: "#D6A23E", label: "Medium" },
  green:  { bg: "#E9F1EA", fg: "#47694F", dot: "#6FA07D", label: "Regular" },
  blue:   { bg: "#EFEBF3", fg: "#6E5E86", dot: "#9A8FB0", label: "Watch" },
  Golden: { bg: "#FBF0D8", fg: "#9A6B18", dot: "#D6A23E", label: "Golden" },
  Silver: { bg: "#ECECEC", fg: "#5A5A5A", dot: "#B0B0B0", label: "Silver" },
  Black:  { bg: "#E3E3E3", fg: "#2B2B2B", dot: "#333333", label: "Black" },
};
const pri = (c) => PRIORITY[c] || { bg: "#F0ECE4", fg: "#8B857A", dot: "#C9C2B5", label: "—" };
const initials = (n = "") =>
  n.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";

function ScotPage({ apiBase = "", authToken }) {
  const [tab, setTab] = useState("today");
  const [sc, setSc] = useState("");   // "" = all coordinators
  const token = authToken || (typeof localStorage !== "undefined" ? localStorage.getItem('access_token') : "");

  const api = useCallback(
    async (path, opts = {}) => {
      // inject ?sc= / &sc= when a coordinator is selected
      if (sc) path += (path.includes("?") ? "&" : "?") + "sc=" + encodeURIComponent(sc);
      const res = await fetch(apiBase + "/api/v1/scot" + path, {
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: "Bearer " + token } : {}) },
        ...opts,
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    [apiBase, token, sc]
  );

  const TABS = [
    ["today", "Today's Calls", "\u260E"],
    ["master", "Master List", "\u25A6"],
    ["schedule", "Monthly Schedule", "\u25A4"],
    ["history", "History", "\u21BA"],
  ];

  return (
    <div className="scot-root">
      <style>{CSS}</style>
      <header className="scot-hero">
        <div className="hero-left">
          <div className="hero-kicker">ALOK INGOTS · SALES</div>
          <h1 className="hero-title">SCOT</h1>
          <p className="hero-sub">Sales Call &amp; Outreach Tracker</p>
        </div>
        <div className="hero-right">
          <label className="sc-switch">
            <span>Coordinator</span>
            <select value={sc} onChange={(e) => setSc(e.target.value)}>
              <option value="">All coordinators</option>
              <option value="Ankita">Ankita ma'am</option>
              <option value="Jyoti">Jyoti ma'am</option>
              <option value="Sumitra">Sumitra ma'am</option>
            </select>
          </label>
        </div>
      </header>

      <nav className="scot-seg">
        {TABS.map(([k, label, ic]) => (
          <button key={k} className={"seg-btn" + (tab === k ? " active" : "")} onClick={() => setTab(k)}>
            <span className="seg-ic">{ic}</span>{label}
          </button>
        ))}
      </nav>

      <main className="scot-main" key={tab + sc}>
        {tab === "today" && <TodayView api={api} />}
        {tab === "master" && <MasterView api={api} sc={sc} />}
        {tab === "schedule" && <ScheduleView api={api} />}
        {tab === "history" && <HistoryView api={api} />}
      </main>
    </div>
  );
}

/* --------------------------------- TODAY --------------------------------- */
function TodayView({ api }) {
  const [data, setData] = useState({ calls: [], date: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api("/today").then(setData).finally(() => setLoading(false));
  }, [api]);
  useEffect(load, [load]);

  const setField = (id, f, v) =>
    setData((d) => ({ ...d, calls: d.calls.map((c) => (c.call_id === id ? { ...c, [f]: v } : c)) }));

  const save = async (c, status) => {
    setSaving(c.call_id);
    try {
      await api("/calls/" + c.call_id, {
        method: "POST",
        body: JSON.stringify({ remark: c.remark || "", reason_pending: c.reason_pending || "", called_by: c.called_by || "", status }),
      });
      setField(c.call_id, "done", status === "done");
      setField(c.call_id, "status", status);
    } finally { setSaving(null); }
  };

  const stats = useMemo(() => {
    const total = data.calls.length;
    const done = data.calls.filter((c) => c.done).length;
    return { total, done, pending: total - done };
  }, [data]);

  if (loading) return <Skeleton />;

  return (
    <div className="fade-up">
      <div className="kpi-row">
        <Kpi label="Calls today" value={stats.total} accent="#C0693F" />
        <Kpi label="Completed" value={stats.done} accent="#5E8A6E" />
        <Kpi label="Pending" value={stats.pending} accent="#C08A2E" />
        <div className="kpi progress-kpi">
          <div className="kpi-label">Progress</div>
          <div className="prog-track"><div className="prog-fill" style={{ width: (stats.total ? (stats.done / stats.total) * 100 : 0) + "%" }} /></div>
          <div className="prog-num">{stats.total ? Math.round((stats.done / stats.total) * 100) : 0}%</div>
        </div>
      </div>

      {!data.calls.length ? (
        <Empty icon="☀️" title="Nothing scheduled" sub="No calls are queued for today." />
      ) : (
        <div className="call-list">
          {data.calls.map((c, i) => {
            const p = pri(c.colour);
            return (
              <article key={c.call_id} className={"call-card" + (c.done ? " is-done" : "")} style={{ animationDelay: i * 40 + "ms" }}>
                <div className="cc-lead">
                  <div className="avatar" style={{ background: p.bg, color: p.fg }}>{initials(c.client_name)}</div>
                  <span className="pri-bar" style={{ background: p.dot }} />
                </div>
                <div className="cc-main">
                  <div className="cc-head">
                    <div>
                      <div className="cc-client">{c.client_name}</div>
                      <div className="cc-contact">{c.contact_person}{c.contact_details ? " · " + c.contact_details : ""}</div>
                    </div>
                    <span className="badge" style={{ background: p.bg, color: p.fg }}>{p.label}</span>
                  </div>
                  {c.grades && (
                    <div className="chip-row">
                      {c.grades.split(",").map((g, gi) => <span key={gi} className="chip">{g.trim()}</span>)}
                    </div>
                  )}
                  <div className="fb-grid">
                    <label className="fb"><span>Remark</span>
                      <input value={c.remark} placeholder="Call outcome…" onChange={(e) => setField(c.call_id, "remark", e.target.value)} />
                    </label>
                    <label className="fb"><span>Reason if pending</span>
                      <input value={c.reason_pending} placeholder="Why not closed…" onChange={(e) => setField(c.call_id, "reason_pending", e.target.value)} />
                    </label>
                    <label className="fb sc"><span>SC</span>
                      <input value={c.called_by} placeholder="EMP0xx" onChange={(e) => setField(c.call_id, "called_by", e.target.value)} />
                    </label>
                  </div>
                </div>
                <div className="cc-actions">
                  <button className="pill ok" disabled={saving === c.call_id} onClick={() => save(c, "done")}>{c.done ? "\u2713 Done" : "Mark done"}</button>
                  <button className="pill ghost" disabled={saving === c.call_id} onClick={() => save(c, "skipped")}>Skip</button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* --------------------------------- MASTER -------------------------------- */
const BLANK_FORM = { owner_sc: "Ankita", client_name: "", contact_person: "", contact_details: "", region: "", group_label: "", colour: "green", frequency: 2, call_days: [] };

function AddCustomerModal({ api, defaultOwner, onClose, onSaved }) {
  const [f, setF] = useState({ ...BLANK_FORM, owner_sc: defaultOwner || "Ankita" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const toggleDay = (d) => setF((s) => ({ ...s, call_days: s.call_days.includes(d) ? s.call_days.filter((x) => x !== d) : [...s.call_days, d] }));

  const save = async () => {
    if (!f.client_name.trim()) { setErr("Client name is required"); return; }
    setBusy(true); setErr("");
    try {
      await api("/customers", { method: "POST", body: JSON.stringify({ ...f, call_days: f.call_days.join(",") }) });
      onSaved();
    } catch (e) { setErr("Could not save — " + e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head"><h3>Add customer</h3><button className="x" onClick={onClose}>\u00D7</button></div>
        <div className="modal-body">
          <label className="mf"><span>Coordinator (owner)</span>
            <select value={f.owner_sc} onChange={(e) => set("owner_sc", e.target.value)}>
              <option value="Ankita">Ankita ma'am</option>
              <option value="Jyoti">Jyoti ma'am</option>
              <option value="Sumitra">Sumitra ma'am</option>
            </select></label>
          <label className="mf"><span>Priority</span>
            <select value={f.colour} onChange={(e) => set("colour", e.target.value)}>
              <option value="red">High</option><option value="yellow">Medium</option>
              <option value="green">Regular</option><option value="blue">Watch</option>
            </select></label>
          <label className="mf full"><span>Client name *</span>
            <input value={f.client_name} onChange={(e) => set("client_name", e.target.value)} autoFocus /></label>
          <label className="mf"><span>Contact person</span>
            <input value={f.contact_person} onChange={(e) => set("contact_person", e.target.value)} /></label>
          <label className="mf"><span>Contact details</span>
            <input value={f.contact_details} onChange={(e) => set("contact_details", e.target.value)} placeholder="phone / email" /></label>
          <label className="mf"><span>City / Region</span>
            <input value={f.region} onChange={(e) => set("region", e.target.value)} placeholder="Mumbai, Chennai…" /></label>
          <label className="mf"><span>Group label</span>
            <input value={f.group_label} onChange={(e) => set("group_label", e.target.value)} placeholder="Group A…" /></label>
          <label className="mf full"><span>Frequency (calls / month) *</span>
            <input type="number" min="1" max="31" value={f.frequency} onChange={(e) => set("frequency", parseInt(e.target.value) || 0)} /></label>
          <div className="mf full"><span>Preferred days to call</span>
            <div className="day-pick">
              {WEEK.map((d) => (
                <button key={d} type="button" className={"day-btn" + (f.call_days.includes(d) ? " on" : "")} onClick={() => toggleDay(d)}>
                  {d[0].toUpperCase() + d.slice(1)}
                </button>
              ))}
            </div>
          </div>
          {err && <div className="mf full err-msg">{err}</div>}
        </div>
        <div className="modal-foot">
          <button className="pill ghost" onClick={onClose}>Cancel</button>
          <button className="pill ok" disabled={busy} onClick={save}>{busy ? "Saving…" : "Add customer"}</button>
        </div>
      </div>
    </div>
  );
}

function AssignRegionBar({ api, onDone }) {
  const [me, setMe] = useState(null);
  const [regions, setRegions] = useState([]);
  const [scs, setScs] = useState([]);
  const [region, setRegion] = useState("");
  const [owner, setOwner] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  useEffect(() => {
    api("/me").then(setMe).catch(() => {});
    api("/regions").then((d) => { setRegions(d.regions || []); setScs(d.scs || []); }).catch(() => {});
  }, [api]);
  if (!me || !me.is_admin) return null;
  const S = { padding: "8px 10px", borderRadius: "8px", border: "1px solid #E9E5DD", background: "#FCFBF8", color: "#2F2A24" };
  const assign = async () => {
    if (!region || !owner) { setMsg("Pick a region and a coordinator"); return; }
    setBusy(true); setMsg("");
    try {
      const r = await api("/assign-region", { method: "POST", body: JSON.stringify({ region: region, owner_sc: owner }) });
      setMsg("Assigned " + r.updated + " customer(s) in " + region + " to " + owner);
      api("/regions").then((d) => setRegions(d.regions || [])).catch(() => {});
      if (onDone) onDone();
    } catch (e) { setMsg("Could not assign — " + e.message); }
    finally { setBusy(false); }
  };
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap", background: "#FFFFFF", border: "1px solid #E9E5DD", borderRadius: "14px", padding: "12px 14px", margin: "0 0 14px" }}>
      <span style={{ fontWeight: 700, color: "#2F2A24" }}>Assign by region</span>
      <select style={S} value={region} onChange={(e) => setRegion(e.target.value)}>
        <option value="">Choose region…</option>
        {regions.map((r) => <option key={r.region} value={r.region}>{r.region} ({r.total})</option>)}
      </select>
      <span style={{ color: "#8B857A" }}>→</span>
      <select style={S} value={owner} onChange={(e) => setOwner(e.target.value)}>
        <option value="">Choose coordinator…</option>
        {scs.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      <button className="cta" disabled={busy} onClick={assign}>{busy ? "Assigning…" : "Assign"}</button>
      {msg && <span style={{ color: "#47694F", fontWeight: 600 }}>{msg}</span>}
    </div>
  );
}


function MasterView({ api, sc }) {
  const [grid, setGrid] = useState({ columns: [], rows: [] });
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  const load = useCallback(() => { setLoading(true); api("/master").then(setGrid).finally(() => setLoading(false)); }, [api]);
  useEffect(load, [load]);

  const cols = useMemo(() => {
    return grid.columns.map((c, ci) => ({ ...c, items: grid.rows.map((r) => r[ci]).filter(Boolean) }));
  }, [grid]);

  const AddBtn = <button className="cta" onClick={() => setAdding(true)}>+ Add customer</button>;

  if (loading) return <Skeleton />;
  if (!cols.length) return (
    <div className="fade-up">
      <div className="master-bar">{AddBtn}</div>
      <Empty icon="📋" title="Master list empty" sub="Add your first customer to build the region groups." />
      {adding && <AddCustomerModal api={api} defaultOwner={sc} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />}
    </div>
  );

  return (
    <div className="fade-up">
      <div className="master-bar">{AddBtn}</div>
      <AssignRegionBar api={api} onDone={load} />
      <div className="board">
      {cols.map((col, i) => (
        <section key={i} className="board-col" style={{ animationDelay: i * 50 + "ms" }}>
          <div className="board-head">
            <div>
              <div className="board-region">{col.region || "Unassigned"}</div>
              <div className="board-group">{col.group_label}</div>
            </div>
            <span className="board-count">{col.items.length}</span>
          </div>
          <div className="board-body">
            {col.items.map((cell, ci) => {
              const p = pri(cell.colour);
              return (
                <div key={ci} className="mini-card">
                  <span className="mini-dot" style={{ background: p.dot }} />
                  <div className="mini-body">
                    <div className="mini-name">{cell.client_name}</div>
                    <div className="mini-meta">
                      <span className="freq">{cell.frequency}\u00D7/mo</span>
                      {cell.call_days && <span className="days">{cell.call_days.split(",").map((d) => d.trim().slice(0, 1).toUpperCase()).join(" ")}</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      ))}
      </div>
      {adding && <AddCustomerModal api={api} defaultOwner={sc} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />}
    </div>
  );
}

/* -------------------------------- SCHEDULE ------------------------------- */
function ScheduleView({ api }) {
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [grid, setGrid] = useState({ header: [], rows: [] });
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api(`/schedule?month=${month}`).then(setGrid).finally(() => setLoading(false));
  }, [api, month]);
  useEffect(load, [load]);
  useEffect(() => { if (toast) { const t = setTimeout(() => setToast(""), 2600); return () => clearTimeout(t); } }, [toast]);

  const generate = async () => {
    setBusy(true);
    const [y, m] = month.split("-");
    try {
      const r = await api(`/schedule/generate?year=${y}&month=${m}`, { method: "POST" });
      load();
      setToast(`Generated ${r.calls_scheduled} calls for ${month}`);
    } finally { setBusy(false); }
  };

  return (
    <div className="fade-up">
      <div className="sched-toolbar">
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="month-input" />
        <button className="cta" disabled={busy} onClick={generate}>
          {busy ? <span className="spin" /> : "\u21BB"} {busy ? "Generating…" : "Generate schedule"}
        </button>
        {toast && <span className="toast">{toast}</span>}
      </div>

      {loading ? <Skeleton /> : !grid.rows.length ? (
        <Empty icon="📅" title="No schedule yet" sub="Pick a month and hit Generate." />
      ) : (
        <div className="grid-wrap">
          <table className="sched-table">
            <thead>
              <tr>
                <th className="stick">Client</th>
                {grid.header.map((h) => (
                  <th key={h.day} className={["sat", "sun"].includes(h.weekday) ? "wknd" : ""}>
                    <span className="d-num">{h.day}</span><span className="d-wd">{h.weekday[0].toUpperCase()}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grid.rows.map((r) => (
                <tr key={r.id}>
                  <td className="stick client-cell">
                    <span className="mini-dot" style={{ background: pri(r.colour).dot }} />
                    <span className="cc-name-sm">{r.client_name}</span>
                  </td>
                  {grid.header.map((h) => {
                    const st = r.days[h.day];
                    return (
                      <td key={h.day} className={["sat", "sun"].includes(h.weekday) ? "wknd" : ""}>
                        {st && <span className={"ycell " + st} />}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* --------------------------------- HISTORY ------------------------------- */
function HistoryView({ api }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api("/history").then(setRows).finally(() => setLoading(false)); }, [api]);

  if (loading) return <Skeleton />;
  if (!rows.length) return <Empty icon="🔄" title="No history yet" sub="Completed calls with remarks show up here." />;

  const fmt = (d) => (typeof d === "string" ? d : new Date(d).toLocaleDateString());
  return (
    <div className="hist-list fade-up">
      {rows.map((r, i) => {
        const p = pri(r.colour);
        return (
          <div key={i} className="hist-row" style={{ animationDelay: Math.min(i, 12) * 30 + "ms" }}>
            <div className="hist-date"><span>{fmt(r.call_date)}</span></div>
            <span className="mini-dot" style={{ background: p.dot }} />
            <div className="hist-body">
              <div className="hist-top"><b>{r.client_name}</b><span className="hist-contact">{r.contact_person}</span></div>
              {r.grades && <div className="chip-row sm">{r.grades.split(",").slice(0, 6).map((g, gi) => <span key={gi} className="chip">{g.trim()}</span>)}</div>}
              <div className="hist-remark">{r.remark || <em>no remark</em>}{r.reason_pending && <span className="hist-reason"> · {r.reason_pending}</span>}</div>
            </div>
            {r.called_by && <span className="sc-tag">{r.called_by}</span>}
          </div>
        );
      })}
    </div>
  );
}

/* ------------------------------- primitives ------------------------------ */
const Kpi = ({ label, value, accent }) => (
  <div className="kpi"><div className="kpi-label">{label}</div><div className="kpi-val" style={{ color: accent }}>{value}</div></div>
);
const Empty = ({ icon, title, sub }) => (
  <div className="empty fade-up"><div className="empty-ic">{icon}</div><h3>{title}</h3><p>{sub}</p></div>
);
const Skeleton = () => (
  <div className="skel-wrap">{[0, 1, 2, 3].map((i) => <div key={i} className="skel" style={{ animationDelay: i * 90 + "ms" }} />)}</div>
);

/* --------------------------------- styles -------------------------------- */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&family=Sora:wght@600;700;800&display=swap');
.scot-root{--accent:#C0693F;--accent2:#A9552E;--sage:#6FA07D;--sage-ink:#47694F;--gold:#C08A2E;
  --bg:#F6F4EF;--surface:#FFFFFF;--line:#E9E5DD;--ink:#2F2A24;--muted:#8B857A;--radius:14px;
  font-family:'Manrope',system-ui,sans-serif;color:var(--ink);background:var(--bg);
  padding:22px 22px 40px;min-height:100%;box-sizing:border-box}
.scot-root *{box-sizing:border-box}

.scot-hero{display:flex;align-items:flex-end;justify-content:space-between;
  background:linear-gradient(135deg,#FCFBF8 0%,#F3EFE7 60%,#EFE9DD 100%);
  border:1px solid #EDE7DB;border-radius:var(--radius);padding:24px 26px;position:relative;overflow:hidden;
  box-shadow:0 10px 26px -18px rgba(120,90,60,.4)}
.scot-hero::after{content:"";position:absolute;right:-40px;top:-60px;width:230px;height:230px;
  background:radial-gradient(circle,rgba(192,105,63,.16),transparent 70%);pointer-events:none}
.hero-kicker{font-size:11px;letter-spacing:.22em;color:var(--accent);font-weight:700}
.hero-title{font-family:'Sora',sans-serif;font-size:38px;font-weight:800;margin:2px 0 0;letter-spacing:.04em;
  color:var(--ink)}
.hero-title::after{content:"";display:block;width:46px;height:4px;border-radius:99px;margin-top:8px;
  background:linear-gradient(90deg,var(--accent),var(--gold))}
.hero-sub{margin:10px 0 0;color:var(--muted);font-size:13.5px;font-weight:600}
.hero-right{position:relative;z-index:1}
.sc-switch{display:flex;flex-direction:column;gap:5px;align-items:flex-end}
.sc-switch span{font-size:10.5px;font-weight:700;color:var(--muted);text-transform:uppercase;letter-spacing:.06em}
.sc-switch select{border:1px solid #E4DCCE;background:#fff;border-radius:10px;padding:9px 14px;font-family:inherit;
  font-size:13.5px;font-weight:600;color:var(--ink);cursor:pointer;box-shadow:0 2px 8px -6px rgba(120,90,60,.4)}
.sc-switch select:focus{outline:none;border-color:var(--accent);box-shadow:0 0 0 3px rgba(192,105,63,.14)}

.scot-seg{display:inline-flex;gap:4px;background:#ECE7DE;padding:5px;border-radius:12px;margin:20px 0 18px;flex-wrap:wrap}
.seg-btn{border:none;background:transparent;padding:9px 18px;border-radius:9px;font-family:inherit;
  font-size:13.5px;font-weight:600;color:var(--muted);cursor:pointer;display:flex;align-items:center;gap:8px;transition:all .2s ease}
.seg-btn:hover{color:var(--accent)}
.seg-btn.active{background:var(--surface);color:var(--ink);box-shadow:0 4px 12px -6px rgba(120,90,60,.35)}
.seg-ic{font-size:14px;opacity:.85}

.scot-main{animation:fadeUp .35s ease both}
.fade-up{animation:fadeUp .4s ease both}
@keyframes fadeUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}}

/* KPIs */
.kpi-row{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin-bottom:20px}
.kpi{background:var(--surface);border:1px solid var(--line);border-radius:var(--radius);padding:16px 18px;
  box-shadow:0 2px 8px -6px rgba(120,90,60,.2)}
.kpi-label{font-size:12px;color:var(--muted);font-weight:600;text-transform:uppercase;letter-spacing:.04em}
.kpi-val{font-family:'Sora',sans-serif;font-size:30px;font-weight:800;margin-top:6px;line-height:1}
.progress-kpi{display:flex;flex-direction:column;justify-content:center}
.prog-track{height:9px;background:#EFEBE3;border-radius:99px;margin:10px 0 6px;overflow:hidden}
.prog-fill{height:100%;background:linear-gradient(90deg,var(--accent),var(--gold));border-radius:99px;transition:width .6s cubic-bezier(.2,.8,.2,1)}
.prog-num{font-size:13px;font-weight:700;color:var(--accent)}

/* Today cards */
.call-list{display:flex;flex-direction:column;gap:12px}
.call-card{display:flex;gap:16px;background:var(--surface);border:1px solid var(--line);border-radius:var(--radius);
  padding:16px 18px;box-shadow:0 2px 10px -8px rgba(120,90,60,.25);animation:fadeUp .4s ease both;transition:box-shadow .2s,transform .2s}
.call-card:hover{box-shadow:0 14px 30px -18px rgba(120,90,60,.4);transform:translateY(-2px)}
.call-card.is-done{opacity:.72;background:#FAF8F3}
.cc-lead{display:flex;align-items:flex-start;gap:10px}
.avatar{width:44px;height:44px;border-radius:12px;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:15px;font-family:'Sora',sans-serif}
.pri-bar{width:4px;align-self:stretch;border-radius:99px}
.cc-main{flex:1;min-width:0}
.cc-head{display:flex;justify-content:space-between;align-items:flex-start;gap:10px}
.cc-client{font-weight:700;font-size:15.5px;color:var(--ink)}
.cc-contact{font-size:12.5px;color:var(--muted);margin-top:1px}
.badge{font-size:11px;font-weight:700;padding:4px 10px;border-radius:99px;white-space:nowrap}
.chip-row{display:flex;flex-wrap:wrap;gap:6px;margin:10px 0 2px}
.chip-row.sm{margin:4px 0}
.chip{font-size:11px;font-weight:600;background:#EDF2ED;color:var(--sage-ink);padding:3px 9px;border-radius:7px;border:1px solid #DCE6DD}
.fb-grid{display:grid;grid-template-columns:1fr 1fr 90px;gap:10px;margin-top:12px}
.fb{display:flex;flex-direction:column;gap:4px}
.fb span{font-size:10.5px;font-weight:700;color:var(--muted);text-transform:uppercase;letter-spacing:.03em}
.fb input{border:1px solid var(--line);border-radius:9px;padding:8px 10px;font-family:inherit;font-size:13px;background:#FBFAF7;transition:border .15s,box-shadow .15s}
.fb input:focus{outline:none;border-color:var(--accent);box-shadow:0 0 0 3px rgba(192,105,63,.14);background:#fff}
.cc-actions{display:flex;flex-direction:column;gap:8px;justify-content:center;min-width:112px}
.pill{border:none;border-radius:10px;padding:9px 12px;font-family:inherit;font-size:13px;font-weight:700;cursor:pointer;transition:all .18s}
.pill.ok{background:var(--accent);color:#fff}
.pill.ok:hover{background:var(--accent2)}
.pill.ghost{background:#F0ECE4;color:#7A7368}
.pill.ghost:hover{background:#E7E1D6}
.pill:disabled{opacity:.5;cursor:default}

/* Master board */
.master-bar{display:flex;justify-content:flex-end;margin-bottom:14px}
.board{display:flex;gap:16px;overflow-x:auto;padding-bottom:8px}
.modal-overlay{position:fixed;inset:0;background:rgba(47,42,36,.32);backdrop-filter:blur(2px);
  display:flex;align-items:center;justify-content:center;z-index:50;animation:fadeUp .2s ease both;padding:20px}
.modal{background:#fff;border-radius:16px;width:min(560px,100%);max-height:90vh;overflow:auto;
  box-shadow:0 30px 70px -30px rgba(47,42,36,.5)}
.modal-head{display:flex;justify-content:space-between;align-items:center;padding:18px 22px;border-bottom:1px solid var(--line)}
.modal-head h3{margin:0;font-family:'Sora',sans-serif;font-size:17px;color:var(--ink)}
.modal-head .x{border:none;background:none;font-size:24px;line-height:1;color:var(--muted);cursor:pointer}
.modal-body{padding:20px 22px;display:grid;grid-template-columns:1fr 1fr;gap:14px}
.mf{display:flex;flex-direction:column;gap:5px}
.mf.full{grid-column:1/-1}
.mf span{font-size:11px;font-weight:700;color:var(--muted);text-transform:uppercase;letter-spacing:.03em}
.mf input,.mf select{border:1px solid var(--line);border-radius:9px;padding:9px 11px;font-family:inherit;font-size:13.5px;background:#FBFAF7}
.mf input:focus,.mf select:focus{outline:none;border-color:var(--accent);box-shadow:0 0 0 3px rgba(192,105,63,.14);background:#fff}
.day-pick{display:flex;flex-wrap:wrap;gap:6px}
.day-btn{border:1px solid var(--line);background:#FBFAF7;border-radius:8px;padding:7px 12px;font-family:inherit;
  font-size:12.5px;font-weight:600;color:var(--muted);cursor:pointer;transition:all .15s}
.day-btn.on{background:var(--accent);color:#fff;border-color:var(--accent)}
.err-msg{color:#B24A2E;font-size:12.5px;font-weight:600;background:#F7E6DE;padding:8px 12px;border-radius:8px}
.modal-foot{display:flex;justify-content:flex-end;gap:10px;padding:16px 22px;border-top:1px solid var(--line)}
.board-col{flex:0 0 260px;background:var(--surface);border:1px solid var(--line);border-radius:var(--radius);
  overflow:hidden;animation:fadeUp .4s ease both;box-shadow:0 2px 10px -8px rgba(120,90,60,.25)}
.board-head{display:flex;justify-content:space-between;align-items:center;padding:14px 16px;
  background:linear-gradient(135deg,#F3EFE7,#EAE4D8);border-bottom:1px solid #E7E0D3}
.board-region{font-size:11px;color:var(--accent);font-weight:700;letter-spacing:.04em}
.board-group{font-family:'Sora',sans-serif;font-size:16px;font-weight:700;color:var(--ink)}
.board-count{background:#EDF2ED;color:var(--sage-ink);border-radius:99px;padding:2px 10px;font-size:12px;font-weight:700}
.board-body{padding:12px;display:flex;flex-direction:column;gap:8px;max-height:66vh;overflow-y:auto}
.mini-card{display:flex;gap:10px;align-items:flex-start;background:#FBFAF7;border:1px solid var(--line);border-radius:10px;padding:10px 12px;transition:all .18s}
.mini-card:hover{background:#fff;border-color:#E0D8C9;box-shadow:0 6px 16px -12px rgba(120,90,60,.45);transform:translateX(2px)}
.mini-dot{width:9px;height:9px;border-radius:50%;margin-top:4px;flex:0 0 auto}
.mini-name{font-weight:700;font-size:13px;line-height:1.25}
.mini-meta{display:flex;gap:8px;margin-top:3px;font-size:11px;color:var(--muted)}
.mini-meta .freq{color:var(--accent);font-weight:700}
.mini-meta .days{letter-spacing:.12em;font-weight:600}

/* Schedule grid */
.sched-toolbar{display:flex;align-items:center;gap:12px;margin-bottom:16px;flex-wrap:wrap}
.month-input{border:1px solid var(--line);border-radius:10px;padding:9px 12px;font-family:inherit;font-size:13.5px;background:#fff}
.month-input:focus{outline:none;border-color:var(--accent);box-shadow:0 0 0 3px rgba(192,105,63,.14)}
.cta{border:none;background:var(--accent);color:#fff;border-radius:10px;padding:10px 18px;font-family:inherit;font-size:13.5px;font-weight:700;cursor:pointer;display:flex;align-items:center;gap:8px;transition:background .18s}
.cta:hover{background:var(--accent2)}.cta:disabled{opacity:.6}
.toast{background:#E9F1EA;color:var(--sage-ink);font-size:12.5px;font-weight:700;padding:7px 14px;border-radius:99px;animation:fadeUp .3s ease both}
.spin{width:14px;height:14px;border:2px solid rgba(255,255,255,.45);border-top-color:#fff;border-radius:50%;animation:sp .7s linear infinite;display:inline-block}
@keyframes sp{to{transform:rotate(360deg)}}
.grid-wrap{overflow:auto;max-height:70vh;border:1px solid var(--line);border-radius:var(--radius);background:#fff}
.sched-table{border-collapse:separate;border-spacing:0;font-size:12px}
.sched-table th{background:#F1ECE3;color:var(--ink);padding:8px 3px;text-align:center;min-width:30px;position:sticky;top:0;z-index:2;border-bottom:1px solid #E7E0D3}
.sched-table th.stick,.sched-table td.stick{position:sticky;left:0;text-align:left;min-width:200px;background:#fff;z-index:1}
.sched-table th.stick{background:#F1ECE3;z-index:3}
.d-num{display:block;font-weight:700;font-size:12px}.d-wd{display:block;font-size:9px;color:var(--muted)}
.sched-table td{border-bottom:1px solid #F1EDE5;border-right:1px solid #F6F3EE;padding:4px;text-align:center;height:34px}
.client-cell{display:flex;align-items:center;gap:8px;font-weight:600}
.cc-name-sm{font-size:12.5px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:160px}
.sched-table tbody tr:hover td{background:#FBF9F5}
.sched-table tbody tr:hover td.stick{background:#FBF9F5}
.wknd{background:#F6F3EE}
.ycell{display:inline-block;width:16px;height:16px;border-radius:5px;background:linear-gradient(135deg,#D08A5E,#C0693F)}
.ycell.done{background:linear-gradient(135deg,#7FA98C,#5E8A6E)}
.ycell.skipped{background:#DCD6CA}

/* History */
.hist-list{display:flex;flex-direction:column;gap:8px}
.hist-row{display:flex;align-items:flex-start;gap:12px;background:var(--surface);border:1px solid var(--line);border-radius:12px;padding:12px 16px;animation:fadeUp .35s ease both;transition:box-shadow .18s}
.hist-row:hover{box-shadow:0 8px 20px -16px rgba(120,90,60,.45)}
.hist-date{flex:0 0 92px;font-size:12px;font-weight:700;color:var(--accent);background:#F5EFE6;border-radius:8px;padding:6px 8px;text-align:center}
.hist-body{flex:1;min-width:0}
.hist-top{display:flex;align-items:baseline;gap:8px}.hist-top b{font-size:14px}
.hist-contact{font-size:12px;color:var(--muted)}
.hist-remark{font-size:13px;margin-top:4px;color:#4A4339}
.hist-remark em{color:var(--muted)}
.hist-reason{color:var(--gold);font-weight:600}
.sc-tag{background:#EDF2ED;color:var(--sage-ink);font-size:11px;font-weight:700;padding:3px 9px;border-radius:99px;align-self:center}

/* empty + skeleton */
.empty{text-align:center;padding:60px 20px;color:var(--muted)}
.empty-ic{font-size:40px;opacity:.5}
.empty h3{margin:12px 0 4px;color:var(--ink);font-family:'Sora',sans-serif}
.empty p{margin:0;font-size:13.5px}
.skel-wrap{display:flex;flex-direction:column;gap:12px}
.skel{height:84px;border-radius:var(--radius);background:linear-gradient(100deg,#EEEAE2 30%,#F7F4EF 50%,#EEEAE2 70%);background-size:200% 100%;animation:shimmer 1.3s ease infinite}
@keyframes shimmer{to{background-position:-200% 0}}

@media(max-width:820px){
  .kpi-row{grid-template-columns:repeat(2,1fr)}
  .fb-grid{grid-template-columns:1fr}
  .call-card{flex-wrap:wrap}
  .cc-actions{flex-direction:row;min-width:0;width:100%}
  .hero-title{font-size:30px}
}
`;

export default ScotPage;
